/**
 * The audit log, its hash chain, the month digests and the job board.
 *
 * **Nothing in this console can edit or delete an event — including a superadmin.** That is what
 * makes the log evidence rather than a report. Each event carries the hash of the one before it, so
 * a broken link is an incident, not a warning: it means something was altered at the storage layer.
 *
 * Sensitive before/after values are redacted **in the record itself**. The log proves the act; it
 * does not leak the data the act touched.
 */
import { useState } from 'react';
import { Button, Chip, StatusChip } from '../components/primitives';
import { DataTable, DetailPage } from '../components/data';
import { KV } from '../components/entities';
import { ConfirmAction, FilterBar, Section } from '../components/kit';
import { QUEUE_HINT, useQueue } from '../lib/useQueue';
import { useConsole } from '../app/console';
import {
  useAuditDigests,
  useAuditEvents,
  useVerifyChain,
  type AuditEventRow,
} from '../lib/api/hooks/audit';
import {
  jobVerdict,
  useErrorTail,
  useJobHealth,
  useJobRuns,
  type JobHealthRow,
} from '../lib/api/hooks/jobs';
import { errorMessage } from '../lib/api/client';
import { useUrlFilters, useUrlSearch, useUrlTab } from '../lib/filterCodecs';
import { formatDateTime } from '../lib/datetime';
import { OpsStatus, RestoreDrills } from './OpsStatus';
import { ResponseCache } from './ResponseCache';

const TABS = [
  { key: 'stream', label: 'Event stream' },
  { key: 'digests', label: 'Chain & digests' },
  { key: 'jobs', label: 'Background jobs' },
];

/** The tab rides the URL (doc 05-admin/04 §3.3), so a pasted link opens the same tab. */
const TAB_KEYS = TABS.map((t) => t.key);

/**
 * One word per job, and the tone that goes with it.
 *
 * ⛔ **`never` is NOT neutral and NOT success.** A job that has never run is the thing this panel
 * exists to surface, so it is tinted `warn` — the operator should look at it. Rendering it green,
 * grey, or as an empty cell is the failure that got this tab deleted the first time.
 *
 * ⛔ **`skipped` is `info`, not `error`.** A job that declined because a kill switch is off did
 * what it was told; colouring it like a failure trains people to ignore the colour.
 */
/* Keyed by the verdict union, not by `string`: the map is TOTAL by construction, so a new
   verdict added to `jobVerdict` fails the typecheck here instead of rendering `undefined`.
   ⛔ A `?? fallback` would have compiled and shown a plausible label for a state nobody
   designed — worse than an error. */
const JOB_VERDICTS: Record<
  ReturnType<typeof jobVerdict>,
  { label: string; tone: 'success' | 'warn' | 'error' | 'info' }
> = {
  never: { label: 'Never run', tone: 'warn' },
  ok: { label: 'Succeeded', tone: 'success' },
  failed: { label: 'Failed', tone: 'error' },
  skipped: { label: 'Skipped', tone: 'info' },
  running: { label: 'Still running', tone: 'info' },
};

/**
 * ⛔ **A missing timestamp renders as a dash with a meaning, never as a blank.** "—" plus the
 * verdict chip beside it says "this has not happened"; an empty cell says nothing at all, and an
 * operator reads nothing at all as fine.
 */
export function jobWhen(value: string | null | undefined): string {
  if (value === null || value === undefined) return '—';
  return formatDateTime(value);
}

/** The count the last run reported. ⛔ `null` means it reported none — not that it did none. */
/**
 * The worker backlog in one line (arch-15 §10): how many jobs are due and how long the oldest
 * has waited, so ops see the "add a worker" trigger approaching. An unreadable backlog says so —
 * a blank or a zero there would read as "nothing queued".
 */
export function backlogLine(health: {
  queue_depth?: number | null;
  oldest_job_seconds?: number | null;
  read_replica_enabled: boolean;
}): string {
  const reads = health.read_replica_enabled ? 'reads on the replica' : 'reads on the primary';
  if (health.queue_depth == null) return `Queue depth unknown — Redis did not answer · ${reads}`;
  const due = `${health.queue_depth} job${health.queue_depth === 1 ? '' : 's'} due`;
  const waited = Math.round(health.oldest_job_seconds ?? 0);
  const oldest =
    health.queue_depth === 0
      ? 'nothing waiting'
      : `oldest waited ${waited < 120 ? `${waited} s` : `${Math.round(waited / 60)} min`}`;
  return `${due} · ${oldest} · ${reads}`;
}

export function jobItems(row: JobHealthRow): string {
  if (row.last_items === null || row.last_items === undefined) return '—';
  return row.last_items.toLocaleString('en-IN');
}

/**
 * The API row, mapped to what the table renders — presentation only, nothing invented.
 *
 * The one judgement call: an actor is `actor_type` plus an id, and an id alone tells an operator
 * nothing, so it renders as `admin#4` rather than `4`, with the admin's name in front when the API
 * resolved one (`Priya Ops (admin#4)`). Only admins are named: a devotee stays `user#id`, because
 * their name is personal data the log never needed.
 */
/**
 * Money and privacy are the two categories an operator scans for, so they carry a tint. Everything
 * else is neutral rather than assigned a colour it does not mean — a log where every row is
 * coloured is a log where no colour means anything.
 */
export function categoryTone(category: string): 'warn' | 'info' | 'neutral' {
  if (category === 'money') return 'warn';
  if (category === 'privacy') return 'info';
  return 'neutral';
}

export interface AuditView {
  id: number;
  at: string;
  actor: string;
  category: string;
  action: string;
  entity: string;
  scopeUsed: string;
  why: string;
  rowHash: string;
  /* The `X-Request-Id` join key to the logs (admin doc 28 §2). */
  requestId: string;
  before: unknown;
  after: unknown;
}

function actorLabel(type: string, id: number | null | undefined, name: string | null | undefined): string {
  if (id === null || id === undefined) return type;
  return name ? `${name} (${type}#${id})` : `${type}#${id}`;
}

export function toAuditView(row: AuditEventRow): AuditView {
  const actorId = row.actor_admin_id ?? row.actor_user_id;
  return {
    id: row.id,
    at: row.created_at,
    actor: actorLabel(row.actor_type, actorId, row.actor_name),
    category: row.category,
    action: row.action,
    /* The public code when there is one — it is what an operator would search for. */
    entity: row.entity_public_code ?? `${row.entity_type}${row.entity_id === null ? '' : `/${row.entity_id}`}`,
    scopeUsed: row.scope_used ?? '—',
    why: row.reason ?? '—',
    rowHash: row.row_hash,
    requestId: row.request_id ?? '—',
    before: row.before_json,
    after: row.after_json,
  };
}

const ENTITY_FILTER = { etype: { kind: 'search' }, eid: { kind: 'id' } } as const;

export function Audit() {
  const { toast } = useConsole();
  const [tab, setTab] = useUrlTab('audit', TAB_KEYS, 'stream');
  const [q, setQ] = useUrlSearch('audit');
  const [confirm, setConfirm] = useState(false);

  /* The log filters server-side by entity code — searching an append-only log client-side would
     search only the page in hand and confidently report nothing. */
  /* A History panel's deep link (doc 03 §6.15): one row's slice, by type and id. */
  const [slice, setSlice] = useUrlFilters('audit', ENTITY_FILTER);
  const stream = useAuditEvents({
    entity_public_code: q.trim() || undefined,
    entity_type: slice.etype,
    entity_id: slice.etype ? slice.eid : undefined,
  });
  const digests = useAuditDigests();
  const verify = useVerifyChain();
  const jobs = useJobHealth();
  /* Which job's history is open. `null` = the list; the query is disabled until a row is
     picked, so opening the tab costs one request rather than one per job. */
  const [openJob, setOpenJob] = useState<string | null>(null);
  const runs = useJobRuns(openJob);
  /* The error tail (arch-09 §10), read only while its tab is open. The server admits superadmin
     alone, which is also the only scope this screen is routed for (`modules.ts`). */
  const errors = useErrorTail(tab === 'jobs');
  const rows = (stream.data?.pages ?? []).flatMap((page) => page.items).map(toAuditView);
  const { focus, setFocus, open, setOpen } = useQueue(rows, { key: q + tab });

  if (open) {
    return (
      <DetailPage
        title={open.action}
        code={open.entity}
        status={<StatusChip tone={categoryTone(open.category)} label={open.category} />}
        onClose={() => setOpen(null)}
        audit="This record is append-only. Nobody, including a superadmin, can edit or remove it."
        actions={
          <Button size="sm" variant="secondary" icon="link">
            Copy event link
          </Button>
        }
      >
        <KV
          rows={[
            ['When', open.at],
            ['Actor', open.actor],
            ['Scope used', open.scopeUsed],
            ['Entity', open.entity],
            ['Reason given', open.why],
            ['Request id', open.requestId],
            /* The row's own hash — the link this event contributes to the chain. */
            ['Row hash', open.rowHash],
          ]}
        />
        <div
          style={{
            border: '1px solid var(--c-border)',
            borderRadius: 'var(--r-tile)',
            background: 'var(--c-sunk)',
            padding: 'var(--sp-md)',
            fontFamily: 'var(--font-mono)',
            fontSize: 'var(--fs-admin-cell)',
            color: 'var(--c-ink2)',
            whiteSpace: 'pre-wrap',
            overflowX: 'auto',
          }}
        >
          {JSON.stringify({ before: open.before, after: open.after }, null, 2)}
        </div>
        <p style={{ margin: 0, fontSize: 'var(--fs-admin-cell)', color: 'var(--c-ink3)' }}>
          Sensitive before/after values are redacted in the audit record itself — the log proves the
          act, it does not leak the data.
        </p>
      </DetailPage>
    );
  }

  return (
    <>
      <div style={{ display: 'flex', gap: 'var(--sp-sm)', flexWrap: 'wrap', alignItems: 'center' }}>
        {TABS.map((t) => (
          <Chip key={t.key} variant="filter" label={t.label} selected={tab === t.key} onClick={() => setTab(t.key)} />
        ))}
        <span style={{ flex: 1 }} />
        <span style={{ fontSize: 'var(--fs-admin-colhead)', color: 'var(--c-ink3)' }}>
          Read-only for finance and support · nothing in the console can edit or delete an event
        </span>
      </div>

      {tab === 'stream' && (
        <>
          <FilterBar
            q={q}
            onQ={setQ}
            placeholder="Actor, action, entity, reason — press /"
            tabs={[
              { key: 'all', label: 'All' },
            ]}
            tab="all"
            right={
              <Button size="sm" variant="secondary" icon="download">
                Export (audited)
              </Button>
            }
          />
          {slice.etype && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-sm)', fontSize: 'var(--fs-admin-cell)' }}>
              <span style={{ color: 'var(--c-ink2)' }}>
                Showing only {slice.etype}
                {slice.eid === undefined ? '' : ` #${slice.eid}`} — opened from its History panel.
              </span>
              <Button size="sm" variant="ghost" onClick={() => setSlice({ etype: undefined, eid: undefined })}>
                Show the whole log
              </Button>
            </div>
          )}
          <DataTable
            density="compact"
            focusedIndex={focus}
            onFocusRow={setFocus}
            onRowClick={(row) => setOpen(rows.find((e) => String(e.id) === String(row.id))!)}
            emptyLabel="No event matches this search"
            footer={
              <>
                <span>
                  {/* A cursor-paged log has no total to count towards, and inventing one would be
                      a number nobody could reconcile. It says what it has and whether there is
                      more. */}
                  {rows.length} loaded{stream.hasNextPage ? ' · more below' : ' · end of the log'}
                </span>
                <span>{QUEUE_HINT}</span>
              </>
            }
            columns={[
              { key: 'at', label: 'When' },
              { key: 'actor', label: 'Actor' },
              { key: 'category', label: 'Category' },
              { key: 'action', label: 'Action', mono: true },
              { key: 'entity', label: 'Entity', mono: true },
              { key: 'scope', label: 'Scope used', mono: true },
              { key: 'why', label: 'Reason given' },
            ]}
            rows={rows.map((e) => ({
              id: e.id,
              /* IST, in the console's one format: the raw `2026-10-29T20:50:00.616000+00:00` read
                 as a different day to an operator in India, and a different hour on every desk. */
              at: formatDateTime(e.at),
              actor: e.actor,
              category: <StatusChip tone={categoryTone(e.category)} label={e.category} />,
              action: e.action,
              entity: e.entity,
              scope: e.scopeUsed,
              why: e.why,
            }))}
          />
        </>
      )}

      {tab === 'digests' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))', gap: 'var(--sp-lg)', alignItems: 'start' }}>
          <Section
            title="Hash chain"
            note="Each event carries the hash of the one before it"
            actions={
              <Button size="sm" variant="secondary" icon="shield-check" onClick={() => setConfirm(true)}>
                Verify chain
              </Button>
            }
          >
            {verify.data ? (
              <KV
                rows={[
                  ['Events checked', verify.data.checked.toLocaleString('en-IN')],
                  [
                    'Chain state',
                    <StatusChip
                      key="s"
                      tone={verify.data.intact ? 'success' : 'error'}
                      label={verify.data.intact ? 'Intact' : 'BROKEN'}
                    />,
                  ],
                  ['What the server said', verify.data.detail],
                  /* Only present when something is wrong, and then it is the whole story. */
                  ...(verify.data.first_broken_id === null || verify.data.first_broken_id === undefined
                    ? []
                    : [['First broken event', String(verify.data.first_broken_id)] as [string, string]]),
                ]}
              />
            ) : (
              <p style={{ margin: 0, fontSize: 'var(--fs-admin-cell)', color: 'var(--c-ink2)' }}>
                {verify.isPending
                  ? 'Recomputing every hash…'
                  : 'The chain has not been verified in this session. Verifying recomputes every hash and is read-only.'}
              </p>
            )}
            <p style={{ margin: 0, fontSize: 'var(--fs-admin-cell)', color: 'var(--c-ink3)' }}>
              A broken link is an incident, not a warning: it means an event was altered or removed
              at the storage layer.
            </p>
          </Section>

          <Section title="Month digests" note="Published so a later dispute cannot re-litigate the log" pad={false}>
            <DataTable
              density="compact"
              columns={[
                { key: 'month', label: 'Month' },
                { key: 'events', label: 'Events', align: 'right', mono: true },
                { key: 'digest', label: 'Digest', mono: true },
                { key: 'status', label: 'State' },
              ]}
              emptyLabel={digests.isPending ? 'Loading digests…' : 'No month has been digested yet'}
              rows={(digests.data?.items ?? []).map((d) => ({
                id: d.month_key,
                month: d.month_key,
                events: d.event_count.toLocaleString('en-IN'),
                digest: d.final_hash,
                status: <StatusChip tone="success" label={`rows ${d.first_row_id}–${d.last_row_id}`} />,
              }))}
            />
          </Section>
        </div>
      )}

      {tab === 'jobs' && (
        <Section
          title="Background jobs"
          note="Every registered job — including the ones that have never run"
          pad={false}
        >
          {jobs.isSuccess && (
            <p
              style={{
                margin: 0,
                padding: 'var(--sp-sm) var(--sp-md)',
                fontSize: 'var(--fs-admin-cell)',
                color: 'var(--c-ink2)',
              }}
            >
              {backlogLine(jobs.data)}
            </p>
          )}
          <DataTable
            density="compact"
            columns={[
              { key: 'job', label: 'Job', mono: true },
              { key: 'state', label: 'Last outcome' },
              { key: 'last_run', label: 'Last run' },
              { key: 'last_success', label: 'Last success' },
              { key: 'items', label: 'Items', align: 'right', mono: true },
            ]}
            /* ⛔ Loading, empty and failed are three different sentences. An error here means the
               panel does not KNOW whether the jobs are running — which must never be shown as
               "no jobs". */
            emptyLabel={
              jobs.isPending
                ? 'Reading job health…'
                : jobs.isError
                  ? `Could not read job health — ${errorMessage(jobs.error)}`
                  : 'No jobs are registered on the worker'
            }
            /* ⛔ `onRowClick` hands over the whole ROW, not its id. Naming the parameter
               `id` and wrapping it in `String()` compiled fine — `String()` takes
               anything — and stored "[object Object]", so the history never opened. */
            onRowClick={(row) => setOpenJob(String(row.id))}
            selected={openJob === null ? [] : [openJob]}
            rows={(jobs.data?.items ?? []).map((row) => {
              const verdict = JOB_VERDICTS[jobVerdict(row)];
              return {
                id: row.job_name,
                job: row.job_name,
                state: <StatusChip tone={verdict.tone} label={verdict.label} />,
                last_run: jobWhen(row.last_run_at),
                last_success: jobWhen(row.last_success_at),
                items: jobItems(row),
              };
            })}
          />
          {openJob !== null && (
            <Section
              title={`${openJob} — last runs`}
              note="Newest first · what the worker actually recorded"
              pad={false}
              actions={
                <Button size="sm" variant="secondary" onClick={() => setOpenJob(null)}>
                  Close
                </Button>
              }
            >
              <DataTable
                density="compact"
                columns={[
                  { key: 'started', label: 'Started' },
                  { key: 'finished', label: 'Finished' },
                  { key: 'outcome', label: 'Outcome' },
                  { key: 'items', label: 'Items', align: 'right', mono: true },
                  { key: 'detail', label: 'Reason / error' },
                ]}
                emptyLabel={
                  runs.isPending
                    ? 'Reading runs…'
                    : runs.isError
                      ? `Could not read runs — ${errorMessage(runs.error)}`
                      : 'This job has never run'
                }
                rows={(runs.data?.items ?? []).map((run) => ({
                  id: String(run.id),
                  started: jobWhen(run.started_at),
                  /* ⛔ A run killed mid-flight keeps `finished_at: null` for ever — the dash is
                     the record of a job that started and never reported back, not a gap. */
                  finished: jobWhen(run.finished_at),
                  outcome: run.outcome,
                  /* `??` rather than an explicit null-or-undefined pair: the schema types this
                     as `number | null`, so the `undefined` arm was a branch nothing could take. */
                  items: run.items ?? '—',
                  /* The server's own words — a failure's message or a skip's reason, never ours. */
                  detail: run.error_text ?? '—',
                }))}
              />
            </Section>
          )}

          <p style={{ margin: 0, fontSize: 'var(--fs-admin-cell)', color: 'var(--c-ink3)' }}>
            Every row here is a real <code>job_run</code> record. A job that has never run says so
            rather than appearing healthy — and a job missing from this list is not registered on
            the worker at all, which is a different problem again.
          </p>

          <Section
            title="Recent errors"
            note="Captured 500s and failed jobs, newest first · the request id finds the full stack"
            pad={false}
          >
            <DataTable
              density="compact"
              columns={[
                { key: 'when', label: 'When' },
                { key: 'where', label: 'Route or job', mono: true },
                { key: 'error', label: 'Error' },
                { key: 'request', label: 'Request id', mono: true },
              ]}
              emptyLabel={
                errors.isPending
                  ? 'Reading recent errors…'
                  : errors.isError
                    ? `Could not read recent errors — ${errorMessage(errors.error)}`
                    : 'Nothing has failed since the last purge'
              }
              rows={(errors.data?.items ?? []).map((row) => ({
                id: String(row.id),
                when: jobWhen(row.created_at),
                where: row.method === 'JOB' ? row.route : `${row.method} ${row.route}`,
                error: `${row.exception_class}: ${row.message}`,
                request: row.request_id,
              }))}
            />
          </Section>

          <OpsStatus />
          <RestoreDrills />
          <ResponseCache />
        </Section>
      )}


      <ConfirmAction
        open={confirm}
        title="Verify the audit chain now?"
        phrase="VERIFY"
        tone="warn"
        consequences={[
          'Recomputes every hash in the log. Read-only, and it may take a while.',
          'A break is reported as an incident and pages the founder channel.',
          'Verification writes its own audit event, as everything here does.',
        ]}
        onCancel={() => setConfirm(false)}
        onConfirm={() => {
          setConfirm(false);
          verify.mutate(undefined, {
            onSuccess: (verdict) =>
              toast({
                tone: verdict.intact ? 'success' : 'error',
                message: verdict.intact
                  ? `Chain verified · ${verdict.checked.toLocaleString('en-IN')} events intact`
                  : `CHAIN BROKEN · ${verdict.detail}`,
              }),
            onError: (e) => toast({ tone: 'error', message: errorMessage(e) }),
          });
        }}
        confirmLabel="Verify chain"
      />
    </>
  );
}
